import React, { useState, useEffect } from "react";
import { Box, Button, TextField, Typography, Grid, Dialog, DialogContent, DialogActions, DialogTitle, IconButton, Paper, CircularProgress, Tooltip, Chip, Divider, } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ImageIcon from "@mui/icons-material/Image";
import SaveIcon from "@mui/icons-material/Save";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

// ─────────────────────────────────────────────
// Shared: Attachment grid (local/new files)
// ─────────────────────────────────────────────
const AttachmentGrid = ({ files = [], onRemove, onPreview, height = "140px" }) => {
    if (!files.length) return null;
    return (
        <Box mt={2}>
            <Grid container spacing={2}>
                {files.map((att, index) => {
                    const src = att?.preview || att?.path || null;
                    const isImage =
                        att?.type?.startsWith("image") ||
                        /\.(jpg|jpeg|png|gif|webp)/i.test(att?.path || "");
                    const isPdf =
                        att?.type === "application/pdf" ||
                        /\.pdf$/i.test(att?.path || att?.name || "");

                    return (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Paper elevation={2} sx={{ p: 1, borderRadius: 2, position: "relative", overflow: "hidden" }}>
                                {onRemove && (
                                    <Tooltip title="Remove">
                                        <IconButton
                                            size="small"
                                            onClick={() => onRemove(index)}
                                            sx={{ position: "absolute", top: 5, right: 5, zIndex: 2, background: "#fff" }}
                                        >
                                            <DeleteIcon fontSize="small" color="error" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {att?.path && (
                                    <Tooltip title="Preview">
                                        <IconButton
                                            size="small"
                                            onClick={() => onPreview && onPreview(att.path)}
                                            sx={{ position: "absolute", top: 5, left: 5, zIndex: 2, background: "#fff" }}
                                        >
                                            <ZoomInIcon fontSize="small" color="primary" />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {isImage ? (
                                    <img
                                        src={src}
                                        alt={att?.originalName || att?.name || "attachment"}
                                        onClick={() => src && onPreview && onPreview(src)}
                                        style={{ width: "100%", height, objectFit: "cover", borderRadius: "8px", cursor: "pointer" }}
                                    />
                                ) : isPdf ? (
                                    src?.startsWith("blob:") ? (
                                        <Box
                                            sx={{ height, borderRadius: "8px", overflow: "hidden", cursor: "pointer" }}
                                            onClick={() => window.open(src, "_blank")}
                                        >
                                            <iframe
                                                src={src}
                                                title={att?.name || "PDF"}
                                                style={{ width: "100%", height: "100%", border: "none", pointerEvents: "none" }}
                                            />
                                        </Box>
                                    ) : (
                                        <Box
                                            onClick={() => att?.path && window.open(att.path, "_blank")}
                                            sx={{
                                                height, display: "flex", flexDirection: "column",
                                                alignItems: "center", justifyContent: "center",
                                                background: "#fff3f3", borderRadius: "8px",
                                                cursor: att?.path ? "pointer" : "default",
                                                border: "1px solid #ffcccc",
                                            }}
                                        >
                                            <PictureAsPdfIcon sx={{ fontSize: 50, color: "#e53935" }} />
                                            <Typography variant="caption" sx={{ color: "#e53935", mt: 0.5 }}>Open PDF</Typography>
                                        </Box>
                                    )
                                ) : (
                                    <Box sx={{ height, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#f5f5f5", borderRadius: "8px" }}>
                                        <ImageIcon sx={{ fontSize: 50, color: "#999" }} />
                                        <Typography variant="caption" sx={{ color: "#999", mt: 0.5 }}>
                                            {att?.name?.split(".").pop()?.toUpperCase() || "File"}
                                        </Typography>
                                    </Box>
                                )}

                                <Typography variant="caption" sx={{ mt: 1, display: "block", textAlign: "center", wordBreak: "break-word" }}>
                                    {att?.originalName || att?.name || att?.fileName || "File"}
                                </Typography>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
};

// ─────────────────────────────────────────────
// Shared: Existing saved attachments (chips)
// ─────────────────────────────────────────────
// const ExistingAttachments = ({ attachments = [], onPreview, label = "Uploaded Files" }) => {
//     if (!attachments.length) return null;
//     return (
//         <Box mt={1.5}>
//             <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
//             <Box display="flex" flexWrap="wrap" gap={1} mt={0.5}>
//                 {attachments.map((att, idx) => {
//                     const isImage =
//                         att?.file_type?.startsWith("image") ||
//                         /\.(jpg|jpeg|png|gif|webp)$/i.test(att?.file_url || "") ||
//                         /\.(jpg|jpeg|png|gif|webp)$/i.test(att?.path || "");
//                     const url = att?.file_url || att?.path || null;
//                     return (
//                         <Chip
//                             key={idx}
//                             icon={<AttachFileIcon />}
//                             label={att?.file_name || att?.originalName || att?.fileName || `Attachment ${idx + 1}`}
//                             size="small"
//                             variant="outlined"
//                             onClick={() => {
//                                 if (isImage && url) onPreview(url);
//                                 else if (url) window.open(url, "_blank");
//                             }}
//                             sx={{ cursor: "pointer" }}
//                         />
//                     );
//                 })}
//             </Box>
//         </Box>
//     );
// };
const ExistingAttachments = ({
    attachments = [],
    onPreview,
    label = "Uploaded Files",
}) => {
    if (!attachments.length) return null;

    return (
        <Box mt={2}>
            <Typography
                variant="subtitle2"
                fontWeight={600}
                mb={1}
            >
                {label}
            </Typography>

            <Grid container spacing={2}>
                {attachments.map((att, idx) => {
                    const url = att?.file_url || att?.path || "";

                    const fileName =
                        att?.file_name ||
                        att?.originalName ||
                        att?.fileName ||
                        `Attachment ${idx + 1}`;

                    const isImage =
                        /\.(jpg|jpeg|png|gif|webp)$/i.test(url);

                    const isPdf =
                        /\.pdf$/i.test(url) ||
                        att?.type === "application/pdf";

                    return (
                        <Grid item xs={12} sm={6} md={3} key={idx}>
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 1,
                                    borderRadius: 2,
                                    position: "relative",
                                    overflow: "hidden",
                                }}
                            >
                                {/* Preview Button */}
                                <Tooltip title="Preview">
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            if (isImage) {
                                                onPreview(url);
                                            } else {
                                                window.open(url, "_blank");
                                            }
                                        }}
                                        sx={{
                                            position: "absolute",
                                            top: 5,
                                            right: 5,
                                            zIndex: 2,
                                            background: "#fff",
                                        }}
                                    >
                                        <ZoomInIcon
                                            fontSize="small"
                                            color="primary"
                                        />
                                    </IconButton>
                                </Tooltip>

                                {/* IMAGE */}
                                {isImage ? (
                                    <img
                                        src={url}
                                        alt={fileName}
                                        onClick={() => onPreview(url)}
                                        style={{
                                            width: "100%",
                                            height: "160px",
                                            objectFit: "cover",
                                            borderRadius: "8px",
                                            cursor: "pointer",
                                        }}
                                    />
                                ) : isPdf ? (
                                    /* PDF */
                                    <Box
                                        onClick={() =>
                                            window.open(url, "_blank")
                                        }
                                        sx={{
                                            height: "160px",
                                            borderRadius: "8px",
                                            overflow: "hidden",
                                            cursor: "pointer",
                                            border: "1px solid #e0e0e0",
                                            background: "#fafafa",
                                        }}
                                    >
                                        <iframe
                                            src={url}
                                            title={fileName}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                border: "none",
                                                pointerEvents: "none",
                                            }}
                                        />
                                    </Box>
                                ) : (
                                    /* OTHER FILES */
                                    <Box
                                        sx={{
                                            height: "160px",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: "#f5f5f5",
                                            borderRadius: "8px",
                                        }}
                                    >
                                        <AttachFileIcon
                                            sx={{
                                                fontSize: 45,
                                                color: "#777",
                                            }}
                                        />

                                        <Typography
                                            variant="caption"
                                            sx={{ mt: 1 }}
                                        >
                                            File
                                        </Typography>
                                    </Box>
                                )}

                                <Typography
                                    variant="caption"
                                    sx={{
                                        mt: 1,
                                        display: "block",
                                        textAlign: "center",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {fileName}
                                </Typography>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
};
// ─────────────────────────────────────────────
// Fullscreen image overlay
// ─────────────────────────────────────────────
const ImagePreviewDialog = ({ src, onClose }) => (
    <Dialog open={!!src} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
            Attachment Preview
            <IconButton onClick={onClose} sx={{ position: "absolute", right: 10, top: 10 }}>
                <CloseIcon />
            </IconButton>
        </DialogTitle>
        <DialogContent>
            {src && (
                <img src={src} alt="preview" style={{ width: "100%", borderRadius: 10, objectFit: "contain" }} />
            )}
        </DialogContent>
    </Dialog>
);

// ─────────────────────────────────────────────
// Old Entry Card — SO + Invoice independently
// ─────────────────────────────────────────────
const OldEntryCard = ({ item, index, modalData, onPreview, invoiceType }) => {

    const showSMFields = invoiceType === "SM";
    const showRCFields = invoiceType === "RC";


    // SO attachment state
    const [soFiles, setSoFiles] = useState([]);
    const [soLoading, setSoLoading] = useState(false);

    // Invoice attachment state
    const [invoiceFiles, setInvoiceFiles] = useState([]);
    const [invoiceLoading, setInvoiceLoading] = useState(false);

    const addFiles = (setter, newFiles) => {
        const mapped = newFiles.map((f) => ({
            file: f,
            name: f.name,
            type: f.type,
            preview: URL.createObjectURL(f),
        }));
        setter((prev) => [...prev, ...mapped]);
    };

    const removeFile = (setter, idx) => {
        if (!window.confirm("Remove this attachment?")) return;
        setter((prev) => prev.filter((_, i) => i !== idx));
    };

    const handleSubmitSo = async () => {
        if (!soFiles.length) {
            window.NotificationUtils?.showError("Please upload at least one Study Material attachment");
            return;
        }
        try {
            setSoLoading(true);
            const payload = new FormData();
            soFiles.forEach((f) => f.file instanceof File && payload.append("files", f.file));
            payload.append("regId", modalData?.regId || "");
            payload.append("partyId", modalData?.partyId || "");
            payload.append("userId", modalData?.userId || "");
            payload.append("entry_id", item?.id || "");
            payload.append("attachment_type", "so");

            const result = await window.Platform.database.updateGenerateInvoiceAttachments(payload);
            if (result?.status) {
                window.NotificationUtils?.showSuccess(result?.message || "Study Material attachments updated");
                setSoFiles([]);
            } else {
                window.NotificationUtils?.showError(result?.message || "Something went wrong");
            }
        } catch (e) {
            console.log(e);
            window.NotificationUtils?.showError("Something went wrong");
        } finally {
            setSoLoading(false);
        }
    };

    const handleSubmitInvoice = async () => {
        if (!invoiceFiles.length) {
            window.NotificationUtils?.showError("Please upload at least one Invoice attachment");
            return;
        }
        try {
            setInvoiceLoading(true);
            const payload = new FormData();
            invoiceFiles.forEach((f) => f.file instanceof File && payload.append("files", f.file));
            payload.append("regId", modalData?.regId || "");
            payload.append("partyId", modalData?.partyId || "");
            payload.append("userId", modalData?.userId || "");
            payload.append("entry_id", item?.id || "");
            payload.append("attachment_type", "invoice");

            const result = await window.Platform.database.updateGenerateInvoiceAttachments(payload);
            if (result?.status) {
                window.NotificationUtils?.showSuccess(result?.message || "Invoice attachments updated");
                setInvoiceFiles([]);
            } else {
                window.NotificationUtils?.showError(result?.message || "Something went wrong");
            }
        } catch (e) {
            console.log(e);
            window.NotificationUtils?.showError("Something went wrong");
        } finally {
            setInvoiceLoading(false);
        }
    };

    // Existing saved attachments — adjust keys per your API response
    const existingSoAttachments = item?.study_material_attachment || [];
    const existingInvoiceAttachments = item?.reg_invoice_attachment || [];

    return (
        <Paper elevation={2} sx={{ p: 2.5, mb: 3, borderRadius: 2, background: "#fafafa", border: "1px solid #e0e0e0" }}>
            <Typography variant="h6" fontWeight={700} mb={2}>
                Previous Entry {index + 1}
            </Typography>

            {/* ── SO Section ── */}
            <Box mb={3}>
                {/* <Typography variant="subtitle1" fontWeight={700} mb={1.5} color="primary">
                    SO Details
                </Typography> */}
                {showSMFields && <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth size="small" disabled label="Study Material Number" value={item?.study_material_invoice_no || item?.study_material_so_no || ""} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth size="small" disabled label="Study Material Amount" value={item?.study_material_amount || item?.study_material_so_amount || ""} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth size="small" disabled type="date" label="Study Material Date"
                            value={item?.so_date ? item.so_date.split("T")[0] : (item?.study_material_invoice_date ? item.study_material_invoice_date.split("T")[0] : "")}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>}

                {/* Existing SO attachments */}
                {showSMFields && <ExistingAttachments attachments={existingSoAttachments} onPreview={onPreview} label="Uploaded SO Attachments" />}
                {/* New SO attachment upload */}
                {/* <Box mt={2}>
                    <Button variant="outlined" component="label" size="small" startIcon={<UploadFileIcon />}>
                        Add SO Attachments
                        <input hidden multiple type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                            onChange={(e) => addFiles(setSoFiles, Array.from(e.target.files))} />
                    </Button>
                </Box> */}

                <AttachmentGrid files={soFiles} onRemove={(i) => removeFile(setSoFiles, i)} onPreview={onPreview} />

                {soFiles.length > 0 && (
                    <Box display="flex" justifyContent="flex-end" mt={1.5}>
                        <Button
                            variant="contained" size="small" color="primary"
                            startIcon={soLoading ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                            onClick={handleSubmitSo}
                            disabled={soLoading}
                        >
                            {soLoading ? "Saving…" : "Save SO Attachments"}
                        </Button>
                    </Box>
                )}
            </Box>


            {/* ── Invoice Section ── */}
            <Box>
                {/* <Typography variant="subtitle1" fontWeight={700} mb={1.5} color="secondary">
                    Invoice Details
                </Typography> */}
                {showRCFields && <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth size="small" disabled label="Invoice Number" value={item?.invoice_number || item?.study_material_invoice_no || ""} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth size="small" disabled label="Invoice Amount" value={item?.invoice_amount || item?.study_material_amount || ""} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth size="small" disabled type="date" label="Invoice Date"
                            value={item?.invoice_date ? item.invoice_date.split("T")[0] : (item?.study_material_invoice_date ? item.study_material_invoice_date.split("T")[0] : "")}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>}

                {/* Existing Invoice attachments */}
                {showRCFields && <ExistingAttachments attachments={existingInvoiceAttachments} onPreview={onPreview} label="Uploaded Invoice Attachments" />}
                {/* New Invoice attachment upload */}
                {/* <Box mt={2}>
                    <Button variant="outlined" component="label" size="small" startIcon={<UploadFileIcon />}>
                        Add Invoice Attachments
                        <input hidden multiple type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                            onChange={(e) => addFiles(setInvoiceFiles, Array.from(e.target.files))} />
                    </Button>
                </Box> */}

                <AttachmentGrid files={invoiceFiles} onRemove={(i) => removeFile(setInvoiceFiles, i)} onPreview={onPreview} />

                {invoiceFiles.length > 0 && (
                    <Box display="flex" justifyContent="flex-end" mt={1.5}>
                        <Button
                            variant="contained" size="small" color="secondary"
                            startIcon={invoiceLoading ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                            onClick={handleSubmitInvoice}
                            disabled={invoiceLoading}
                        >
                            {invoiceLoading ? "Saving…" : "Save Invoice Attachments"}
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Discrepancy Remark */}
            {item?.despute_remark && (
                <Box mt={2} pt={2} borderTop="1px solid #e0e0e0">
                    <Typography variant="subtitle2" fontSize={15} fontWeight={600}>
                        Discrepancy Remark: <span style={{ color: "#ff0000" }}>{item.despute_remark}</span>
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const AddInvoiceOlympiad = ({ closePopup, modalData, invoiceType }) => {

    // Show SM fields only when type is "SM"
    const showSMFields = invoiceType === "SM";

    // Show RC fields only when type is "RC"  
    const showRCFields = invoiceType === "RC";

    const [oldData, setOldData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    // New SO form
    const [soForm, setSoForm] = useState({ sm_number: "", sm_amount: "", sm_date: "" });
    const [soAttachments, setSoAttachments] = useState([]);

    // New Invoice form
    const [invoiceForm, setInvoiceForm] = useState({ invoice_number: "", invoice_amount: "", invoice_date: "" });
    const [invoiceAttachments, setInvoiceAttachments] = useState([]);
    const [openRemarkModal, setOpenRemarkModal] = useState(false);
    const [remark, setRemark] = useState("");

    useEffect(() => {
        if (modalData?.regId) getOldData();
    }, [modalData?.regId]);

    const getOldData = async () => {
        try {
            setHistoryLoading(true);
            const result = await window.Platform.database.getGenerateInvoiceDetails({
                regId: modalData?.regId,
                partyId: modalData?.partyId,
            });
            setOldData(result?.status ? result?.data || [] : []);
        } catch (e) {
            console.log(e);
            setOldData([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    const addFiles = (setter, files) => {
        const mapped = files.map((f) => ({ file: f, name: f.name, type: f.type, preview: URL.createObjectURL(f) }));
        setter((prev) => [...prev, ...mapped]);
    };

    const removeFile = (setter, idx) => {
        if (!window.confirm("Are you sure you want to remove this attachment?")) return;
        setter((prev) => prev.filter((_, i) => i !== idx));
    };

    // const validateAndSubmit = async () => {
    //     if (!soForm.so_number?.trim()) {
    //         window.NotificationUtils?.showError("Please enter SO Number");
    //         return;
    //     }
    //     if (!soForm.so_amount?.toString().trim()) {
    //         window.NotificationUtils?.showError("Please enter SO Amount");
    //         return;
    //     }
    //     if (!soForm.so_date?.trim()) {
    //         window.NotificationUtils?.showError("Please select SO Date");
    //         return;
    //     }
    //     if (!soAttachments.length) {
    //         window.NotificationUtils?.showError("Please upload at least one SO attachment");
    //         return;
    //     }
    //     if (!invoiceForm.invoice_number?.trim()) {
    //         window.NotificationUtils?.showError("Please enter Invoice Number");
    //         return;
    //     }
    //     if (!invoiceForm.invoice_amount?.toString().trim()) {
    //         window.NotificationUtils?.showError("Please enter Invoice Amount");
    //         return;
    //     }
    //     if (!invoiceForm.invoice_date?.trim()) {
    //         window.NotificationUtils?.showError("Please select Invoice Date");
    //         return;
    //     }
    //     if (!invoiceAttachments.length) {
    //         window.NotificationUtils?.showError("Please upload at least one Invoice attachment");
    //         return;
    //     }

    //     try {
    //         setLoading(true);
    //         const payload = new FormData();

    //         // SO files
    //         soAttachments.forEach((f) => f.file instanceof File && payload.append("regInvoiceAttachment", f.file));
    //         // Invoice files
    //         invoiceAttachments.forEach((f) => f.file instanceof File && payload.append("studyMaterialAttachment", f.file));

    //         payload.append("regId", modalData?.regId || "");
    //         payload.append("partyId", modalData?.partyId || "");
    //         payload.append("userId", modalData?.userId || "");

    //         // SO fields
    //         payload.append("study_material_invoice_no", soForm.so_number);
    //         payload.append("study_material_amount", soForm.so_amount);
    //         payload.append("study_material_invoice_date", soForm.so_date);

    //         // Invoice fields
    //         payload.append("reg_invoice_no", invoiceForm.invoice_number);
    //         payload.append("reg_invoice_amount", invoiceForm.invoice_amount);
    //         payload.append("reg_invoice_date", invoiceForm.invoice_date);
    //         payload.append("so_check", modalData?.registration_type == 2 ? "no" : "yes");

    //         const result = await window.Platform.database.createGenerateInvoice(payload);

    //         if (result?.status) {
    //             window.NotificationUtils?.showSuccess(result?.message || "Added successfully");
    //             getOldData();
    //             setSoForm({ sm_number: "", sm_amount: "", sm_date: "" });
    //             setSoAttachments([]);
    //             setInvoiceForm({ invoice_number: "", invoice_amount: "", invoice_date: "" });
    //             setInvoiceAttachments([]);
    //             closePopup();
    //         } else {
    //             window.NotificationUtils?.showError(result?.message || "Something went wrong");
    //         }
    //     } catch (e) {
    //         console.log(e);
    //         window.NotificationUtils?.showError("Something went wrong");
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const validateAndSubmit = async () => {

        // =========================
        // CHECK SO FORM
        // =========================
        const isSoFilled =
            soForm.so_number?.trim() ||
            soForm.so_amount?.toString().trim() ||
            soForm.so_date?.trim() ||
            soAttachments.length;

        // =========================
        // CHECK INVOICE FORM
        // =========================
        const isInvoiceFilled =
            invoiceForm.invoice_number?.trim() ||
            invoiceForm.invoice_amount?.toString().trim() ||
            invoiceForm.invoice_date?.trim() ||
            invoiceAttachments.length;

        // =========================
        // AT LEAST ONE REQUIRED
        // =========================
        if (!isSoFilled && !isInvoiceFilled) {
            window.NotificationUtils?.showError(
                "Please fill either Invoice details or Study Material details"
            );
            return;
        }

        // =========================
        // INVOICE VALIDATION
        // =========================
        if (isInvoiceFilled) {
            if (!invoiceForm.invoice_number?.trim()) {
                window.NotificationUtils?.showError("Please enter Invoice Number");
                return;
            }

            if (!invoiceForm.invoice_amount?.toString().trim()) {
                window.NotificationUtils?.showError("Please enter Invoice Amount");
                return;
            }

            if (!invoiceForm.invoice_date?.trim()) {
                window.NotificationUtils?.showError("Please select Invoice Date");
                return;
            }

            if (!invoiceAttachments.length) {
                window.NotificationUtils?.showError(
                    "Please upload at least one Invoice attachment"
                );
                return;
            }
        }

        // =========================
        // SO VALIDATION
        // =========================
        if (isSoFilled) {
            if (!soForm.so_number?.trim()) {
                window.NotificationUtils?.showError("Please enter Study Material Number");
                return;
            }

            if (!soForm.so_amount?.toString().trim()) {
                window.NotificationUtils?.showError("Please enter Study Material Amount");
                return;
            }

            if (!soForm.so_date?.trim()) {
                window.NotificationUtils?.showError("Please select Study Material Date");
                return;
            }

            if (!soAttachments.length) {
                window.NotificationUtils?.showError(
                    "Please upload at least one Study Material attachment"
                );
                return;
            }
        }

        if (!remark?.trim()) {
            window.NotificationUtils.showError("Please enter remark");
            return;
        }
        if (remark?.trim()?.length <= 20) {
            window.NotificationUtils.showError("Remark Must be 20 Characters");
            return;
        }


        try {
            setLoading(true);

            const payload = new FormData();

            // SO files
            soAttachments.forEach((f) => {
                if (f.file instanceof File) {
                    payload.append("regInvoiceAttachment", f.file);
                }
            });

            // Invoice files
            invoiceAttachments.forEach((f) => {
                if (f.file instanceof File) {
                    payload.append("studyMaterialAttachment", f.file);
                }
            });

            payload.append("regId", modalData?.regId || "");
            payload.append("partyId", modalData?.partyId || "");
            payload.append("userId", modalData?.userId || "");

            // SO fields
            payload.append("study_material_invoice_no", soForm.so_number || "");
            payload.append("study_material_amount", soForm.so_amount || "");
            payload.append("study_material_invoice_date", soForm.so_date || "");

            // Invoice fields
            payload.append("reg_invoice_no", invoiceForm.invoice_number || "");
            payload.append("reg_invoice_amount", invoiceForm.invoice_amount || "");
            payload.append("reg_invoice_date", invoiceForm.invoice_date || "");
            payload.append("remark", remark);
            payload.append("invoiceType", invoiceType);

            payload.append(
                "so_check",
                modalData?.registration_type == 2 ? "no" : "yes"
            );

            const result = await window.Platform.database.createGenerateInvoice(payload);

            if (result?.status) {
                window.NotificationUtils?.showSuccess(
                    result?.message || "Added successfully"
                );

                getOldData();

                setSoForm({
                    so_number: "",
                    so_amount: "",
                    so_date: "",
                });

                setSoAttachments([]);

                setInvoiceForm({
                    invoice_number: "",
                    invoice_amount: "",
                    invoice_date: "",
                });

                setInvoiceAttachments([]);

                closePopup();
            } else {
                window.NotificationUtils?.showError(
                    result?.message || "Something went wrong"
                );
            }
        } catch (e) {
            console.log(e);
            window.NotificationUtils?.showError("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box p={3} position="relative">
            {/* Header */}
            <Typography variant="h5" fontWeight={700} textAlign="center" mb={1}>Add Invoice</Typography>
            <Box sx={{ borderBottom: "1px solid #000", mb: 2, pb: 1 }}>
                <Typography sx={{ fontSize: "15px" }}>
                    <b>RNO ID:</b> {modalData?.rno_id} |
                    <b style={{ marginLeft: 10 }}> RM Name:</b> {modalData?.userName} |
                    <b style={{ marginLeft: 10 }}> Party Name:</b> {modalData?.schoolName} ({modalData?.partyId})
                </Typography>
            </Box>
            <IconButton onClick={closePopup} sx={{ position: "absolute", top: 12, right: 12 }}>
                <CloseIcon />
            </IconButton>

            {/* Old Entries */}
            {historyLoading ? (
                <Box textAlign="center" py={3}><CircularProgress /></Box>
            ) : (
                oldData.map((item, index) => (
                    <OldEntryCard
                        key={item?.id || index}
                        item={item}
                        index={index}
                        modalData={modalData}
                        onPreview={setPreviewImage}
                        invoiceType={invoiceType}
                    />
                ))
            )}

            {/* New Entry Form */}
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3, mt: 1 }}>
                <Typography variant="h6" fontWeight={700} >Add New Entry</Typography>

                {/* ── Invoice Section ── */}
                {showRCFields && <Grid container spacing={2} mb={1}>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth size="small" label="Invoice Number" value={invoiceForm.invoice_number}
                            onChange={(e) => setInvoiceForm((p) => ({ ...p, invoice_number: e.target.value }))}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth size="small" type="number" label="Invoice Amount" value={invoiceForm.invoice_amount}
                            onChange={(e) => setInvoiceForm((p) => ({ ...p, invoice_amount: e.target.value }))}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth size="small" type="date" label="Invoice Date" value={invoiceForm.invoice_date}
                            onChange={(e) => setInvoiceForm((p) => ({ ...p, invoice_date: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                            Upload Attachment
                            <input hidden multiple type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                                onChange={(e) => addFiles(setInvoiceAttachments, Array.from(e.target.files))} />
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <AttachmentGrid files={invoiceAttachments} onRemove={(i) => removeFile(setInvoiceAttachments, i)} onPreview={setPreviewImage} />
                    </Grid>
                </Grid>}

                {/* ── SO Section ── */}
                {showSMFields && <Grid container spacing={2} mb={1}>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth size="small" label="Study Material Number" value={soForm.so_number}
                            onChange={(e) => setSoForm((p) => ({ ...p, so_number: e.target.value }))}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth size="small" type="number" label="Study Material Amount" value={soForm.so_amount}
                            onChange={(e) => setSoForm((p) => ({ ...p, so_amount: e.target.value }))}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth size="small" type="date" label="Study Material Date" value={soForm.so_date}
                            onChange={(e) => setSoForm((p) => ({ ...p, so_date: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                            Upload Attachment
                            <input hidden multiple type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                                onChange={(e) => addFiles(setSoAttachments, Array.from(e.target.files))} />
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <AttachmentGrid files={soAttachments} onRemove={(i) => removeFile(setSoAttachments, i)} onPreview={setPreviewImage} />
                    </Grid>
                </Grid>}

                {/* Actions */}
                <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                    <Button variant="outlined" color="secondary" onClick={closePopup} disabled={loading}>Cancel</Button>
                    {/* <Button variant="contained" onClick={validateAndSubmit} disabled={loading}>
                        {loading ? <CircularProgress size={22} color="inherit" /> : "Submit"}
                    </Button> */}
                    <Button variant="contained" onClick={() => setOpenRemarkModal(true)} disabled={loading}>
                        {loading ? "Submitting..." : "Submit"}
                    </Button>
                </Box>
            </Paper>

            {/* Image Preview Dialog */}
            <ImagePreviewDialog src={previewImage} onClose={() => setPreviewImage(null)} />

            <Dialog
                open={openRemarkModal}
                onClose={() => setOpenRemarkModal(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Add Remark</DialogTitle>

                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Enter Remark"
                        value={remark}
                        onChange={(e) => setRemark(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenRemarkModal(false)}>
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={validateAndSubmit}
                        disabled={loading}
                    >
                        {loading ? "Submitting..." : "Submit"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AddInvoiceOlympiad;